
import React, { useEffect, useState, Suspense, lazy, memo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/common/Layout';
import gsap from 'gsap';
import Splitting from 'splitting';
import 'splitting/dist/splitting.css';
import Loader from './components/Loader';
// import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Lazy load pages (only loaded when visited)
const Home = lazy(() => import('./pages/home'));
const About = lazy(() => import('./pages/About'));

const CaseStudy = lazy(() => import('./pages/CaseStudy'));
const TermsAndCondition = lazy(() =>
  import('./pages/termsAndcondition')
);
const PrivacyandPolicy = lazy(() =>
  import('./pages/privacyAndPolicy')
);
const Disclaimer = lazy(() =>
  import('./pages/disclaimer')
);
const CookiePolicy = lazy(() =>
  import('./pages/cookiePolicy')
);

// const Cursor = lazy(() => import('./components/cursor')); // optional if you re-enable cursor

// Suppress ResizeObserver errors (same logic, just slightly optimized)
const suppressResizeObserverErrors = () => {
  if (window._resizeObserverErrorSuppressed) return;
  const originalError = console.error;
  console.error = (...args) => {
    const msg = args[0]?.toString() || '';
    if (msg.includes('ResizeObserver loop')) return;
    originalError(...args);
  };
  window._resizeObserverErrorSuppressed = true;
};

function App() {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouchDevice = () => {
      const isTouch =
        window.matchMedia('(pointer: coarse)').matches ||
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0;
      setIsTouchDevice(isTouch);
    };
    checkTouchDevice();

    const mediaQuery = window.matchMedia('(pointer: coarse)');
    mediaQuery.addEventListener?.('change', checkTouchDevice);
    return () => mediaQuery.removeEventListener?.('change', checkTouchDevice);
  }, []);

  useEffect(suppressResizeObserverErrors, []);

  useEffect(() => {
    const checkAndHandleBlankPage = () => {
      const currentPath = window.location.pathname;
      const navigationSource = sessionStorage.getItem('navigationSource');
      if ((currentPath === '/work' || currentPath === '/about') && navigationSource === 'caseStudy') {
        sessionStorage.removeItem('navigationSource');
        setTimeout(() => {
          if (!document.querySelector('.App')?.children?.length) {
            window.location.reload();
          }
        }, 500);
      }
    };

    checkAndHandleBlankPage();
    const handlePopState = () => setTimeout(checkAndHandleBlankPage, 100);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  useEffect(() => {
    const handlePopState = () => {
      const currentPath = window.location.pathname;
      if (['/caseStudy', '/work'].includes(currentPath)) {
        setTimeout(() => window.location.reload(), 50);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <Router>
      <AppContent isTouchDevice={isTouchDevice} />
    </Router>
  );
}

const AppContent = memo(({ isTouchDevice }) => {
  const location = useLocation();
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const colors = ['#0C917E', '#C6D32C', '#7C669B', '#BCA883', '#D7213E', '#E66C79', '#D395B2', '#6E7D9C'];
      const rollovers = document.querySelectorAll('.rollover');
      rollovers.forEach((rollover) => {
        Splitting({ target: rollover, by: 'chars' });
        const chars = rollover.querySelectorAll('.char');
        const originalColors = Array.from(chars).map((c) => window.getComputedStyle(c).color);
        const handleEnter = () =>
          chars.forEach((char) =>
            gsap.to(char, {
              color: colors[Math.floor(Math.random() * colors.length)],
              delay: Math.random() * 0.4,
              duration: 0.2,
              ease: 'power2.out',
            })
          );
        const handleLeave = () =>
          gsap.to(chars, {
            color: (i) => originalColors[i],
            duration: 0.3,
            ease: 'power1.in',
            stagger: 0.001,
          });

        rollover.addEventListener('mouseenter', handleEnter);
        rollover.addEventListener('mouseleave', handleLeave);
        rollover._cleanup = () => {
          rollover.removeEventListener('mouseenter', handleEnter);
          rollover.removeEventListener('mouseleave', handleLeave);
        };
      });
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.querySelectorAll('.rollover').forEach((r) => r._cleanup?.());
    };
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop />
      <div className="App">
        <Layout>
          {/* <ErrorBoundary> */}
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/work" element={<Home />} />
                {/* <Route path="/option2" element={<Option2 />} />
                <Route path="/option3" element={<Option3 />} /> */}
                <Route path="/caseStudy" element={<CaseStudy />} />
                <Route path="/termscondition" element={<TermsAndCondition />} />
                <Route path="/PrivacyandPolicy" element={<PrivacyandPolicy />} />
                <Route path="/Disclaimer" element={<Disclaimer />} />
                <Route path="/cookiePolicy" element={<CookiePolicy />} />
                <Route path="*" element={<Navigate to="/work" replace />} />
              </Routes>
            </Suspense>
          {/* </ErrorBoundary> */}
        </Layout>
      </div>
    </>
  );
});

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo({ top: 0, behavior: 'instant' }), [pathname]);
  return null;
};

export default App;
